import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import { CurrentContextService } from '../../core/context/current-context.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { InventoryCostingApiService, InventoryCostRevaluation } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-revaluations',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule, SelectModule, AinCanAccessDirective],
  template: `
    <ain-page [title]="t('catalog.costing.revaluations.title')" [description]="t('catalog.costing.revaluations.description')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.revaluations.help.title'"
        [descriptionKey]="'catalog.costing.revaluations.help.description'"
        [bulletKeys]="['catalog.costing.revaluations.help.bullets.workflow', 'catalog.costing.revaluations.help.bullets.approve']" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.costing.states.noPermission')" [message]="t('catalog.costing.states.noPermissionMessage')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.costing.manage'" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
          </div>
          <label class="filter">
            <span>{{ t('columns.status') }}</span>
            <p-select [options]="statusOptions()" [(ngModel)]="statusFilter" optionLabel="label" optionValue="value" [showClear]="true" appendTo="body" (ngModelChange)="load()" />
          </label>

          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.costing.revaluations.empty')" />
          } @else {
            <p-table [value]="rows()" [paginator]="true" [rows]="15" dataKey="id">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('catalog.inventory.reference') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('columns.currency') }}</th>
                  <th>{{ t('catalog.costing.revaluations.lineCount') }}</th>
                  <th>{{ t('catalog.costing.revaluations.effectiveAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.referenceNumber }}</button></td>
                  <td>{{ statusLabel(row.status) }}</td>
                  <td>{{ row.currencyCode }}</td>
                  <td>{{ row.lines.length }}</td>
                  <td>{{ row.effectiveAtUtc }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; }
    .toolbar { display: flex; gap: 8px; margin-bottom: 10px; }
    .filter { display: grid; gap: 6px; max-width: 240px; margin-bottom: 10px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryRevaluationsComponent implements OnInit {
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  readonly rows = signal<InventoryCostRevaluation[]>([]);
  statusFilter = '';

  statusOptions(): { label: string; value: string }[] {
    return [
      { label: this.t('catalog.costing.revaluations.status.Draft'), value: 'Draft' },
      { label: this.t('catalog.costing.revaluations.status.Approved'), value: 'Approved' },
      { label: this.t('catalog.costing.revaluations.status.Posted'), value: 'Posted' },
      { label: this.t('catalog.costing.revaluations.status.Cancelled'), value: 'Cancelled' }
    ];
  }

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load(false)
    );
    this.load();
  }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.costingApi.listRevaluations(this.statusFilter || undefined).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.loading.set(false);
      }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/inventory/costing/revaluations/new']);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/inventory/costing/revaluations', id]);
  }

  statusLabel(status: string): string {
    const key = `catalog.costing.revaluations.status.${status}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
